import React, { ReactElement } from 'react';
import { useQuery } from '@apollo/client';
import Panel from '../../ui/Panel/Panel';
import Spinner from '../../ui/Spinner/Spinner';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import useOrderTracking from '../../../hooks/useOrderTracking';
import { useTheme } from '../../../hooks/useTheme';
import ResizableMap from './ResizableMap';
import getCourierLocation from './graphql';
import getMaptiler from '../PickupLocation/components/MaptilerMap/graphql';
import styles from './LocalDeliveryMap.module.css';

export interface LocalDeliveryMapProps {
  featureName?: string;
  fixedPanelHeight?: string;
}

const FETCH_COURIER_LOCATION_POLL_INTERVAL = 30000;

const LocalDeliveryMap = ({
  fixedPanelHeight,
}: LocalDeliveryMapProps): ReactElement => {
  const { spinnerPrimaryNone } = useTheme();

  const {
    state: { carrier, trackingNumbers },
  } = usePageContext();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const maptiler = useQuery(getMaptiler);

  const trackingNumber = trackingNumbers?.[0];
  const { data: courierLocationData } = useQuery(getCourierLocation, {
    variables: {
      courier: carrier ?? '',
      trackingNumber: trackingNumber ?? '',
      limit: 1,
    },
    pollInterval: FETCH_COURIER_LOCATION_POLL_INTERVAL,
    skip: typeof trackingNumber !== 'string' || !currentShipment,
  });

  if (maptiler.loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner s={spinnerPrimaryNone} />
      </div>
    );
  }

  const maptilerKey = maptiler.data?.maptiler?.key;

  if (!currentShipment || maptilerKey === undefined) {
    return <></>;
  }

  const { status, statusCode, lastUpdate } = currentShipment;

  if (status === 'HPUPICKEDUP') {
    return <></>;
  }

  const isHPU = [
    'HPUSCHEDULED',
    'HPUEXCEPTION',
    'HPUPICKUPINPROGRESS',
  ].includes(status ?? '');

  const isMapview = ['MVINTRANSIT', 'MVDELIVERED', 'MVEXCEPTION'].includes(
    status ?? '',
  );

  const showOrigin =
    [
      'SHPD_SDD_101',
      'SHPD_SDD_201',
      'SHPD_SDD_280',
      'SHPD_SDD_281',
      'EXCP_SDD_800',
    ].includes(statusCode ?? '') || isMapview;

  const { origin } = currentShipment;
  const originLng =
    showOrigin && typeof origin?.lng === 'string' ? +origin.lng : undefined;
  const originLat =
    showOrigin && typeof origin?.lat === 'string' ? +origin.lat : undefined;

  const { destination } = currentShipment;
  const destinationLng =
    !isHPU && typeof destination?.lng === 'string'
      ? +destination.lng
      : undefined;
  const destinationLat =
    !isHPU && typeof destination?.lat === 'string'
      ? +destination.lat
      : undefined;

  const showCourier =
    [
      'SHPD_SDD_280',
      'SHPD_SDD_281',
      'INTR_SDD_311',
      'OFDL_SDD_400',
      'OFDL_SDD_403',
      'DLVD_SDD_500',
    ].includes(statusCode ?? '') || isMapview;

  const courierLocationEvents =
    courierLocationData?.courierLocation?.courierLocationEvents;

  const courierLng = showCourier
    ? courierLocationEvents && courierLocationEvents[0]?.longitude
    : undefined;
  const courierLat = showCourier
    ? courierLocationEvents && courierLocationEvents[0]?.latitude
    : undefined;

  // show map if any set of coords is available
  const hasAnyLocation =
    (originLng && originLat) ||
    (destinationLng && destinationLat) ||
    (courierLng && courierLat);

  const height = fixedPanelHeight ?? (hasAnyLocation ? '280px' : '0px');

  const timestamp = [
    (courierLocationEvents && courierLocationEvents[0]?.eventTimestamp) ?? '',
    lastUpdate ?? '',
  ].sort()[1];
  let lastUpdated;
  if (timestamp) {
    const time = new Date(timestamp).toLocaleString('en-US', {
      hour: 'numeric',
      hour12: true,
      minute: 'numeric',
    });
    lastUpdated = `Last updated ${time}`;
  } else {
    lastUpdated = 'Last updated: Unavailable';
  }

  return (
    <Panel
      fixedPanelHeight={height}
      sOuter={{ border: 'none' }}
      sInner={{ padding: 0 }}
      header={<></>}
    >
      {hasAnyLocation && (
        <ResizableMap
          maptilerKey={maptilerKey}
          originLng={originLng}
          originLat={originLat}
          destinationLng={destinationLng}
          destinationLat={destinationLat}
          courierLng={courierLng}
          courierLat={courierLat}
          lastUpdated={lastUpdated}
          isHPU={isHPU}
        />
      )}
    </Panel>
  );
};

export default LocalDeliveryMap;
